import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  alpha,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  ChipProps,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { SATELLITE_MOVE_WINDOW_DAYS } from "encamp-shared/src/constants/wasteAccumulation";
import {
  AccumulationAreaListItemFragment,
  VolumeStatus,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import pluralize from "pluralize";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { formatDaysRemaining } from "routes/Customer/Waste/Containers/containerDisplay";
import { formatDateLikeForDisplay } from "util/dates";

const NO_VALUE_PLACEHOLDER = "";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** A `null` threshold means the facility is exempt from that cap (VSQG), not
 * a zero allowance — so this copy replaces the number wherever a threshold
 * would otherwise render. Today a VSQG is the only way `exempt` can be true;
 * if a future ruleset ever exempts another status, the API should send the
 * reason alongside the flag rather than this copy guessing at it. */
const NO_LIMIT_COPY = "No limit (VSQG exempt)";

/** What to show in place of a null threshold: the exemption if the API
 * asserted one, otherwise the neutral placeholder. */
function noLimitLabel(exempt: boolean): string {
  return exempt ? NO_LIMIT_COPY : NO_VALUE_PLACEHOLDER;
}

/** The list query's row shape. */
export type AreaCardArea = AccumulationAreaListItemFragment;

/** "Jul 1, 2026", or the em-dash placeholder when the date is unset. */
function formatAreaDate(date: string | null | undefined): string {
  if (!date) {
    return NO_VALUE_PLACEHOLDER;
  }
  return formatDateLikeForDisplay(date, { format: "MMM d, yyyy" }) || NO_VALUE_PLACEHOLDER;
}

export const STATUS_COLOR: Record<VolumeStatus, "success" | "warning" | "error"> = {
  [VolumeStatus.Ok]: "success",
  [VolumeStatus.Approaching]: "warning",
  [VolumeStatus.Over]: "error",
};

export const STATUS_LABEL: Record<VolumeStatus, string> = {
  [VolumeStatus.Ok]: "Room to accumulate",
  [VolumeStatus.Approaching]: "Approaching limit",
  [VolumeStatus.Over]: "Over threshold",
};

/**
 * The same statuses, worded for a satellite area.
 *
 * A satellite area over its limit has one remedy — move the waste to central
 * accumulation, and within three days — so the label says that instead of just
 * naming the state. Deliberately NOT folded into `STATUS_LABEL`: that map is
 * also read for central areas, where "move to central" is nonsense.
 */
export const SATELLITE_STATUS_LABEL: Record<VolumeStatus, string> = {
  ...STATUS_LABEL,
  [VolumeStatus.Over]: "At capacity — move to central",
};

/** Severity levels for the central nearest-deadline clockpill. */
type DeadlineLevel = "neutral" | "warning" | "error";

/** Clockpill Chip color per level, following the app's own chip-tinting idiom
 * (see `WasteContainerStatusChip`): an outlined Chip with the MUI semantic
 * `color`, `undefined` for the plain neutral outline. */
const CLOCKPILL_COLOR: Record<DeadlineLevel, ChipProps["color"]> = {
  neutral: undefined,
  warning: "warning",
  error: "error",
};

/** Leading dot color per clockpill level; `null` (no dot) for neutral. */
const CLOCKPILL_DOT: Record<DeadlineLevel, string | null> = {
  neutral: null,
  warning: "warning.main",
  error: "error.main",
};

/**
 * Days remaining until `deadline`, rounded up so "later today" still reads as 1.
 * Goes negative once the deadline has passed: a missed move window is a worse
 * fact than an imminent one, and clamping it to zero would report the two the
 * same way.
 */
function daysLeft(deadline: string, now: Date): number {
  const diffMs = new Date(deadline).getTime() - now.getTime();
  return Math.ceil(diffMs / MS_PER_DAY);
}

/** Semantic level for the central nearest-deadline pill as the deadline nears. */
function deadlineLevel(date: string | null | undefined, now: Date): DeadlineLevel {
  if (!date) {
    return "neutral";
  }
  const days = daysLeft(date, now);
  if (days <= 7) {
    return "error";
  }
  if (days <= 30) {
    return "warning";
  }
  return "neutral";
}

/** A small filled circle in a given theme color (an sx-resolvable color path,
 * e.g. "warning.main"), for status lines / pills. */
function Dot({ color, size = 7 }: { color: string; size?: number }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
  );
}

/**
 * The bordered pill gauge from the prototype. A styled `LinearProgress` so it
 * keeps `role="progressbar"` + aria-valuenow/min/max and an aria-label for
 * free; only the visual rendering changes. The track is the theme's standard
 * "resting surface" token with a hairline divider border; the fill is a
 * theme semantic color path (e.g. "success.main") resolved via `sx`.
 */
function Gauge({
  value,
  fill,
  ariaLabel,
  testId,
}: {
  value: number;
  fill: string;
  ariaLabel: string;
  testId?: string;
}) {
  return (
    <LinearProgress
      variant="determinate"
      value={value}
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{
        height: "0.75rem",
        borderRadius: "99px",
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
        "& .MuiLinearProgress-bar": { backgroundColor: fill, borderRadius: "99px" },
      }}
    />
  );
}

/** The tinted "Satellite" / "Central" type chip pinned to the card header.
 * "Satellite" reads the theme's `primary` slot (Blue Sapphire in the app's
 * light theme — the same brand teal the prototype hardcoded, but via the
 * standard semantic palette instead of a one-off custom token); "Central"
 * reads the theme's neutral resting-surface tokens. */
function TypeChip({ type }: { type: "Satellite" | "Central" }) {
  return (
    <Chip
      label={type}
      size="small"
      sx={(theme) => {
        const tint =
          type === "Satellite"
            ? {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                border: alpha(theme.palette.primary.main, 0.3),
              }
            : {
                bgcolor: theme.palette.action.hover,
                color: theme.palette.text.primary,
                border: theme.palette.divider,
              };
        return {
          flexShrink: 0,
          fontWeight: 700,
          bgcolor: tint.bgcolor,
          color: tint.color,
          border: "1px solid",
          borderColor: tint.border,
        };
      }}
    />
  );
}

/**
 * The progress bars + status copy shared by the card and the area detail
 * page's volume section. Takes only the volume shape (not the whole area) so
 * a detail-page query selecting a different row type still fits.
 *
 * `variant="compact"` (the default, used by `AreaCard`) shows the non-acute
 * bar plus a numeric-only acute row. `variant="detailed"` (the area detail
 * page) shows the acute component as its own second bar instead, since the
 * spec wants both volume components visible as thresholded gauges there.
 */
export function SatelliteVolumeGauge({
  volume,
  areaName,
  variant = "compact",
}: {
  volume: AreaCardArea["volume"];
  areaName: string;
  variant?: "compact" | "detailed";
}) {
  const { nonAcute, acute, status: volumeStatus, moveDeadline, exempt } = volume;
  const threshold = nonAcute.threshold;
  const hasNonAcuteThreshold = threshold != null;
  const percent =
    hasNonAcuteThreshold && threshold > 0 ? Math.round((nonAcute.total / threshold) * 100) : 0;
  const progressValue = Math.min(100, percent);
  const nonAcuteColor = `${STATUS_COLOR[nonAcute.status]}.main`;
  const statusColor = `${STATUS_COLOR[volumeStatus]}.main`;
  const acuteColor = `${STATUS_COLOR[acute.status]}.main`;
  const hasAcuteVolume = acute.totalVolume > 0 || (acute.totalKg ?? 0) > 0;
  const acuteVolumeThreshold = acute.volumeThreshold;
  const acuteKgThreshold = acute.kgThreshold;
  // The API derives both acute caps from one limit object, so they are null
  // together; requiring both keeps a half-set pair from rendering as capped.
  const hasAcuteThreshold = acuteVolumeThreshold != null && acuteKgThreshold != null;
  const acutePercent =
    hasAcuteThreshold && acuteVolumeThreshold > 0
      ? Math.round((acute.totalVolume / acuteVolumeThreshold) * 100)
      : 0;
  const acuteProgressValue = Math.min(100, acutePercent);
  const showAcuteRow = variant === "compact" && hasAcuteVolume;
  const showAcuteDetails = variant === "detailed" && hasAcuteVolume;
  const showAcuteBar = showAcuteDetails && hasAcuteThreshold;
  const acuteAmounts = hasAcuteThreshold
    ? `${acute.totalVolume} / ${acuteVolumeThreshold} gal · ${acute.totalKg ?? NO_VALUE_PLACEHOLDER} / ${acuteKgThreshold} kg`
    : `${acute.totalVolume} gal · ${acute.totalKg ?? NO_VALUE_PLACEHOLDER} kg${exempt ? ` — ${NO_LIMIT_COPY}` : ""}`;

  // The area's status is the worst of its components, so citing the non-acute
  // 55-gal limit unconditionally is wrong when only the acute component tripped.
  // Build the alert copy from whichever component(s) are actually OVER. A
  // component with no threshold (VSQG-exempt) can never legitimately be OVER,
  // so its string is only ever built when a real threshold exists.
  const overParts: string[] = [];
  if (nonAcute.status === VolumeStatus.Over && hasNonAcuteThreshold) {
    overParts.push(`Over ${threshold} gal`);
  }
  if (acute.status === VolumeStatus.Over && hasAcuteThreshold) {
    overParts.push(`Over the acute limit (${acuteVolumeThreshold} gal / ${acuteKgThreshold} kg)`);
  }
  const overDescription = overParts.length > 0 ? overParts.join(" and ") : "Over threshold";
  const daysRemaining = moveDeadline ? daysLeft(moveDeadline, new Date()) : 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography component="div">
          <Box
            component="span"
            sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}
          >
            {nonAcute.total}
          </Box>{" "}
          <Box
            component="span"
            sx={{ typography: "body2", fontWeight: 600, color: "text.secondary" }}
          >
            {hasNonAcuteThreshold ? `/ ${threshold} gal` : `gal · ${noLimitLabel(exempt)}`}
          </Box>
        </Typography>
        {hasNonAcuteThreshold && (
          <Box component="span" sx={{ typography: "caption", fontWeight: 800, color: statusColor }}>
            {percent}%
          </Box>
        )}
      </Stack>
      {hasNonAcuteThreshold && (
        <Gauge
          value={progressValue}
          fill={nonAcuteColor}
          ariaLabel={`${areaName} non-acute volume ${percent}% of threshold`}
        />
      )}
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Dot color={statusColor} />
        <Box component="span" sx={{ typography: "caption", fontWeight: 600, color: statusColor }}>
          {SATELLITE_STATUS_LABEL[volumeStatus]}
        </Box>
      </Stack>
      {showAcuteRow && (
        <Typography variant="body2" sx={{ color: acuteColor }} data-testid="acute-row">
          {acuteAmounts}
        </Typography>
      )}
      {showAcuteDetails && (
        <>
          {showAcuteBar && (
            <Gauge
              value={acuteProgressValue}
              fill={acuteColor}
              ariaLabel={`${areaName} acute volume ${acutePercent}% of threshold`}
              testId="acute-bar"
            />
          )}
          <Typography variant="body2" sx={{ color: acuteColor }} data-testid="acute-caption">
            {acuteAmounts}
          </Typography>
        </>
      )}
      {volumeStatus === VolumeStatus.Over && moveDeadline && (
        <Alert
          severity="error"
          icon={<WarningAmberRounded sx={{ fontSize: "1rem" }} />}
          sx={{ typography: "caption", fontWeight: 700, borderRadius: "0.5625rem", py: 1 }}
        >
          {overDescription} — move to central within {SATELLITE_MOVE_WINDOW_DAYS}{" "}
          {pluralize("day", SATELLITE_MOVE_WINDOW_DAYS)} · {formatDaysRemaining(daysRemaining)}
        </Alert>
      )}
    </>
  );
}

/** Card header: bold area name over a muted location line, type chip right. */
function AreaCardHeader({
  name,
  location,
  type,
}: {
  name: string;
  location?: string | null;
  type: "Satellite" | "Central";
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <Box>
        <Typography
          component="div"
          variant="subtitle2"
          sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.2 }}
        >
          {name}
        </Typography>
        {location && (
          <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {location}
          </Typography>
        )}
      </Box>
      <TypeChip type={type} />
    </Stack>
  );
}

/** A bold figure over a small grey label, used across the central card's row. */
function CardFigure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 72 }}>
      {children}
      <Typography
        component="div"
        variant="caption"
        sx={{ fontWeight: 600, color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

/** Card surface shared by both variants: standard outlined MUI card with a
 * subtle elevation on hover; over-threshold areas get the theme warning
 * border.
 *
 * `data-copy-id` marks this card as the block representing one accumulation
 * area, so the palette's "Pick ID from page" mode can hover it the way it
 * hovers data grid rows. No type attribute: the cache already identifies the
 * id as a `WasteAccumulationArea`. */
function AreaCardShell({
  areaId,
  over,
  onClick,
  ariaLabel,
  children,
}: {
  areaId: string;
  over?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      data-copy-id={areaId}
      sx={{
        height: "100%",
        borderColor: over ? "warning.light" : "divider",
        transition: "box-shadow .15s, border-color .15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardActionArea
        component="div"
        onClick={onClick}
        aria-label={ariaLabel}
        sx={{ height: "100%" }}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>{children}</Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function SatelliteAreaCard({ area, onClick }: { area: AreaCardArea; onClick: () => void }) {
  const threshold = area.volume.nonAcute.threshold;
  const thresholdCopy = threshold != null ? `${threshold} gal` : noLimitLabel(area.volume.exempt);
  const isOver = area.volume.status === VolumeStatus.Over;

  return (
    <AreaCardShell areaId={area.id} over={isOver} onClick={onClick} ariaLabel={area.name}>
      <AreaCardHeader name={area.name} location={area.description} type="Satellite" />
      <SatelliteVolumeGauge volume={area.volume} areaName={area.name} />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        sx={{
          typography: "caption",
          color: "text.secondary",
          borderTop: "1px solid",
          borderColor: "divider",
          pt: 1.25,
        }}
      >
        <span>{pluralize("container", area.containerCount, true)}</span>
        <span>
          Threshold{" "}
          <Box component="b" sx={{ color: "text.primary", fontWeight: 700 }}>
            {thresholdCopy}
          </Box>{" "}
          · across streams
        </span>
      </Stack>
    </AreaCardShell>
  );
}

function CentralAreaCard({ area, onClick }: { area: AreaCardArea; onClick: () => void }) {
  const totalOnSite = area.volume.nonAcute.total + area.volume.acute.totalVolume;
  const level = deadlineLevel(area.nearestDeadlineDate, new Date());
  const pillDot = CLOCKPILL_DOT[level];

  return (
    <AreaCardShell areaId={area.id} onClick={onClick} ariaLabel={area.name}>
      <AreaCardHeader name={area.name} location={area.description} type="Central" />
      <Stack direction="row" spacing={2.25} flexWrap="wrap" useFlexGap>
        <CardFigure label="Containers">
          <Typography
            component="div"
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1 }}
          >
            {area.containerCount}
          </Typography>
        </CardFigure>
        <CardFigure label="Gal on site">
          <Typography
            component="div"
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1 }}
          >
            {totalOnSite}
          </Typography>
        </CardFigure>
        <CardFigure label="Nearest deadline">
          <Chip
            data-testid="nearest-deadline"
            size="small"
            variant="outlined"
            color={CLOCKPILL_COLOR[level]}
            icon={pillDot ? <Dot color={pillDot} size={6} /> : undefined}
            label={formatAreaDate(area.nearestDeadlineDate)}
            sx={{ alignSelf: "flex-start", fontWeight: 800 }}
          />
        </CardFigure>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        alignItems="center"
        sx={{
          typography: "caption",
          color: "text.secondary",
          borderTop: "1px solid",
          borderColor: "divider",
          pt: 1.25,
        }}
      >
        {area.oldestMovedToCentralDate ? (
          <span>
            Oldest moved{" "}
            <Box component="b" sx={{ color: "text.primary", fontWeight: 700 }}>
              {formatAreaDate(area.oldestMovedToCentralDate)}
            </Box>
          </span>
        ) : (
          <span />
        )}
        <span>Time-governed</span>
      </Stack>
    </AreaCardShell>
  );
}

export function AreaCard({ area }: { area: AreaCardArea }) {
  const navigate = useNavigate();
  const handleClick = () => navigate(`../accumulation-areas/${area.id}`);

  if (area.type === WasteAccumulationAreaType.Central) {
    return <CentralAreaCard area={area} onClick={handleClick} />;
  }
  return <SatelliteAreaCard area={area} onClick={handleClick} />;
}
